import * as Yup from 'yup';
import axios from '@/utils/api/axios';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import { ShowIf } from '@/utils/ui/showIf';
import Stack from '@mui/material/Stack';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Unstable_Grid2';
import { useBoolean } from '@/hooks/state/use-boolean';
import LoadingButton from '@mui/lab/LoadingButton';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSettingsContext } from '@/components/layout/settings';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DialogError } from '@/components/utils/errors/dialogs/dialog-error';
import FormProvider, { RHFAutocomplete } from '@/components/forms/hook-form';
import { List, Button, ListItem, CardHeader, ListItemIcon } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { v4 as uuidV4 } from 'uuid';
import {
  IOrderData,
  OrderTypes,
  ICustomMilestone,
  IOrganizationItem,
  IOrderItem as IOrder,
} from '@/types';
import {
  IMilestoneSelected,
  DEFAULT_BILLING_FILTERS,
} from '@/components/data-display/billing-milestones/types';
import { useSnackbar } from 'notistack';
import * as Sentry from '@sentry/react';
import { isEqual } from 'lodash';
import moment from 'moment-timezone';
import { createBillingOrderFirstStepSchema } from '../schemas/billing-order-first-step-schema';
import BillingMilestonesSelector from '@/utils/billing/billing-milestones-selector';
import {
  SelectedMilestone,
  getSelectedMilestonesByIds,
} from '@/utils/billing/billing-milestones-snackbar';

import { OrganizationInfoCard } from '@/components/data-display/organization/organization-info-card';
import { IOrderItem, ChoicesOrderItemType } from '@/sections/management/accounts/orders/context';
import { prepareFinalCustomMilestones } from '@/sections/management/accounts/orders/utils';

const DEFAULT_FILTERS = {
  ...DEFAULT_BILLING_FILTERS,
  groupByMonth: true,
  includeDetails: true,
  groupBySubscription: true,
};

export const processItems = (
  initialItems: IOrderItem[]
): Record<ChoicesOrderItemType, IOrderItem[]> => {
  const initialAccumulator: Record<ChoicesOrderItemType, IOrderItem[]> = {
    newResource: [],
    renewal: [],
    upgrade: [],
    addLicenses: [],
  };

  initialItems.reduce((acc, item) => {
    const { orderItemType } = item;
    item.tempId = uuidV4();

    if (item.customizeBillingMilestones) {
      item.customMilestones =
        item.customMilestones
          ?.sort(
            (a: ICustomMilestone, b: ICustomMilestone) =>
              moment(a.paymentDate).utc().toDate().getTime() -
              moment(b.paymentDate).utc().toDate().getTime()
          )
          ?.map((milestone: ICustomMilestone) => ({
            ...milestone,
            paymentDate: moment(milestone.paymentDate).utc(),
          })) || [];

      item.finalCustomMilestones = prepareFinalCustomMilestones(item.customMilestones);
    }

    acc[orderItemType].push(item);
    return acc;
  }, initialAccumulator);
  return initialAccumulator;
};

type Props = {
  currentOrder?: IOrder;
  currentOrganization?: { _id: string; name?: string };
  onNext: (data: Partial<IOrderData>) => void;
  onCancel: () => void;
  organizations?: any[];
};

async function validateMilestonesInUse(
  organizationId: string,
  milestoneIds: string[],
  currentOrderId?: string
) {
  const { data } = await axios.post(`/organizations/${organizationId}/validate-milestones-in-use`, {
    milestoneIds,
    currentOrderId,
  });
  return data;
}
//TODO: Remove this component and their children after the QA Validations
export const BillingOrderNewEditFirstStepForm = ({
  currentOrder,
  currentOrganization,
  onCancel,
  onNext,
  organizations = [],
}: Props) => {
  const { t } = useTranslation();

  const [selectedBillingMilestones, setSelectedBillingMilestones] = useState<string[]>([]);
  const [loadedSelectedMilestones, setLoadedSelectedMilestones] = useState<SelectedMilestone[]>([]);
  const [enrichedBillingDocs, setEnrichedBillingDocs] = useState<IMilestoneSelected[]>([]);
  const [organizationData, setOrganizationData] = useState<IOrganizationItem | null>(null);
  const [inUseMilestones, setInUseMilestones] = useState<string[]>([]);

  const [error, setError] = useState<any | null>(null);

  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const settings = useSettingsContext();
  const dialogError = useBoolean();
  const changeValidator = useBoolean(false);
  const itemsCurrentOrder = useMemo(
    () => [
      ...(currentOrder?.items ?? []),
      ...(currentOrder?.itemsToAddLicenses ?? []),
      ...(currentOrder?.itemsToRenewal ?? []),
      ...(currentOrder?.itemsToUpgrade ?? []),
    ],
    [
      currentOrder?.items,
      currentOrder?.itemsToAddLicenses,
      currentOrder?.itemsToRenewal,
      currentOrder?.itemsToUpgrade,
    ]
  );

  const fetchOrganizationData = useCallback(
    (orgId: string) => {
      if (!orgId) {
        setOrganizationData(null);
        return;
      }
      axios
        .get(`organizations/${orgId}`, { params: { populate: true } })
        .then((response) => {
          setOrganizationData(response.data.data);
        })
        .catch((err) => {
          enqueueSnackbar(t('Error fetching organization data'), { variant: 'error' });
        });
    },
    [enqueueSnackbar, t]
  );

  useEffect(() => {
    if (currentOrganization?._id) {
      fetchOrganizationData(currentOrganization._id);
    } else if (currentOrder?.organization?._id) {
      fetchOrganizationData(currentOrder.organization._id);
    }
  }, [currentOrganization?._id, currentOrder?.organization?._id, fetchOrganizationData]);

  const shouldLoadMilestones = useMemo(
    () =>
      (currentOrder?.milestones?.length ?? 0) > 0 &&
      !!organizationData?._id &&
      (currentOrder?.selectedMilestones?.length ?? 0) === 0,
    [
      currentOrder?.milestones?.length,
      organizationData?._id,
      currentOrder?.selectedMilestones?.length,
    ]
  );

  const milestones = useMemo(() => {
    if (!currentOrder?.milestones?.length) return [];

    return currentOrder.milestones
      .map((milestone) => (typeof milestone === 'string' ? milestone : milestone._id))
      .filter((id): id is string => Boolean(id));
  }, [currentOrder?.milestones]);

  useEffect(() => {
    if (!shouldLoadMilestones || milestones.length === 0) {
      return;
    }

    let isCancelled = false;

    const loadMilestoneDetails = async () => {
      try {
        if (!organizationData?._id) return;
        const endpoint = `/organizations/${organizationData._id}/billing`;
        const requestBody = {
          page: 1,
          pageSize: 100,
          filter: DEFAULT_FILTERS,
        };

        const response = await axios.post(endpoint, requestBody);

        if (isCancelled) return;

        setEnrichedBillingDocs(response?.data?.docs || response?.data || []);

        const milestoneObjects = getSelectedMilestonesByIds(milestones, response.data.docs || []);

        if (milestoneObjects.length > 0) {
          setLoadedSelectedMilestones(milestoneObjects);
        }
      } catch (loadError) {
        if (!isCancelled) {
          setLoadedSelectedMilestones([]);
          enqueueSnackbar(t('Unexpected error loading billing data'), { variant: 'error' });
          Sentry.captureException(loadError, {
            tags: { component: 'BillingOrderNewEditFirstStepForm', action: 'loadMilestoneDetails' },
          });
        }
      }
    };

    loadMilestoneDetails();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLoadMilestones, milestones, organizationData?._id]);

  const orderSchema = createBillingOrderFirstStepSchema(t);

  const defaultValues = useMemo(() => {
    const {
      newResource: items,
      renewal: itemsToRenewal,
      upgrade: itemsToUpgrade,
      addLicenses: itemsToAddLicenses,
    } = processItems(itemsCurrentOrder ?? []);

    const finalSelectedMilestones =
      (currentOrder?.selectedMilestones?.length ?? 0) > 0
        ? currentOrder?.selectedMilestones
        : loadedSelectedMilestones;

    return {
      organization: organizationData || null,
      items,
      itemsToRenewal,
      itemsToUpgrade,
      itemsToAddLicenses,
      acceptedTermsAndConditions: currentOrder?.acceptedTermsAndConditions ?? false,
      status: currentOrder?.status === 'provisioning' ? 'completed' : currentOrder?.status,
      isMarket: false,
      onlySave: false,
      orderType: null,
      milestones: currentOrder?.milestones ?? [],
      selectedMilestones: finalSelectedMilestones,
    };
  }, [currentOrder, itemsCurrentOrder, organizationData, loadedSelectedMilestones]);

  const methods = useForm({
    resolver: yupResolver<any>(orderSchema),
    mode: 'onSubmit',
    defaultValues,
  });

  const {
    watch,
    handleSubmit,
    setValue,
    formState: { isSubmitting, errors },
    reset,
  } = methods;

  const values = watch();
  const { organization } = values;

  const watchedValues = useMemo(
    () => ({
      organization: values.organization,
    }),
    [values.organization]
  );

  const prevWatchedValues = useRef(watchedValues);

  useEffect(() => {
    if (!isEqual(prevWatchedValues.current, watchedValues)) {
      changeValidator.onTrue();
    }
    prevWatchedValues.current = watchedValues;
  }, [changeValidator, watchedValues]);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  useEffect(() => {
    if (loadedSelectedMilestones.length > 0) {
      setValue('selectedMilestones', loadedSelectedMilestones);
    }
  }, [loadedSelectedMilestones, setValue]);

  useEffect(() => {
    setSelectedBillingMilestones(values.milestones || []);
  }, [values.milestones]);

  useEffect(() => {
    const organizationId = organizationData?._id;
    const selectedCount = values?.selectedMilestones?.length || 0;
    if (!organizationId || selectedCount === 0) return;

    let isCancelled = false;

    const fetchEnrichedDocs = async () => {
      try {
        const endpoint = `/organizations/${organizationId}/billing`;
        const requestBody = {
          page: 1,
          pageSize: 100,
          filter: DEFAULT_FILTERS,
        };

        const response = await axios.post(endpoint, requestBody);
        if (isCancelled) return;

        setEnrichedBillingDocs(response?.data?.docs || response?.data || []);
      } catch (e) {
        enqueueSnackbar(t('Unexpected error loading billing data'), { variant: 'error' });
        Sentry.captureException(e as any, {
          tags: { component: 'BillingOrderNewEditFirstStepForm', action: 'fetchEnrichedDocs' },
        });
      }
    };

    fetchEnrichedDocs();
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationData?._id, values?.selectedMilestones]);

  useEffect(() => {
    if (currentOrganization) {
      setValue('organization', {
        _id: currentOrganization._id,
        label: `${currentOrganization.name}`,
        name: `${currentOrganization.name}`,
      });
    }
  }, [currentOrganization, setValue]);

  const handleMilestonesChange = useCallback(
    (data: { milestones: string[]; orderType: string; selectedMilestones: any[] }) => {
      setSelectedBillingMilestones(data.milestones);
      setValue('orderType', data.orderType);
      setValue('milestones', data.milestones);
      setValue('selectedMilestones', data.selectedMilestones);
    },
    [setValue]
  );

  const queryClient = useQueryClient();

  const { mutate, isLoading } = useMutation({
    mutationFn: async (data: any) => {
      const baseOrderData = {
        ...data,
        milestones: selectedBillingMilestones,
        orderType: data.orderType,
      };

      if (data.onlySave) {
        const orderData = {
          order: {
            ...baseOrderData,
            organization: data?.organization?._id ?? null,
          },
        };

        if (currentOrder?._id) {
          await axios.patch(`orders/${currentOrder._id}`, orderData);
        } else {
          await axios.post('orders', orderData);
        }

        enqueueSnackbar(t('Update success!'));
        queryClient.invalidateQueries({ queryKey: ['billing-order', currentOrder?._id, 'edit'] });
        queryClient.invalidateQueries({ queryKey: ['billing-orders'] });
        reset();
        return onCancel();
      }

      if (changeValidator.value) {
        baseOrderData.changeValidator = true;
      }

      const finalSelectedMilestones =
        data.selectedMilestones?.length > 0 ? data.selectedMilestones : loadedSelectedMilestones;

      const nextData = {
        order: {
          ...baseOrderData,
          selectedMilestones: finalSelectedMilestones,
          // Pass enriched backend response to Step 3 if available
          enrichedBillingDocs,
        },
      };

      return onNext(nextData);
    },
    onError: (mutationError) => {
      setError(mutationError);
      dialogError.onTrue();
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await validateMilestonesInUse(
        data.organization._id,
        data.milestones,
        currentOrder?._id
      );

      if (!result.valid) {
        enqueueSnackbar(t('Some of the milestones are being used in another Billing Order'), {
          variant: 'error',
        });

        const availableMilestones = data.milestones.filter(
          (id: any) => !result.milestonesInUse.includes(id)
        );

        setValue('milestones', availableMilestones);
        setSelectedBillingMilestones(availableMilestones);
        setInUseMilestones(result.milestonesInUse);

        return;
      }

      setInUseMilestones([]);
      mutate(data);
    } catch (submitError) {
      console.error('Form submission error:', submitError);
      enqueueSnackbar(t('An error occurred validating the milestones'), { variant: 'error' });
    }
  });

  const { OVNumber } = currentOrder || {};

  return (
    <>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        {/* Organization Selection - Only show when no organization is provided */}
        <ShowIf condition={!currentOrganization && organizations.length > 0}>
          <Card sx={{ px: 2, pb: 3, mb: 3 }}>
            <CardHeader sx={{ px: 0, pb: 3 }} title={t('Company Information')} />
            <Box
              rowGap={3}
              columnGap={1}
              display="grid"
              sx={{ mb: 2 }}
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(1, 1fr)',
              }}
            >
              <RHFAutocomplete
                name="organization"
                label={`${t('Company')} *`}
                placeholder={t('Search and select a company...')}
                options={
                  Array.isArray(organizations)
                    ? organizations.map((item: any) => ({
                        _id: item._id,
                        label: `${item.name}`,
                        name: `${item.name}`,
                      }))
                    : Array.isArray((organizations as any)?.data)
                      ? (organizations as any).data.map((item: any) => ({
                          _id: item._id,
                          label: `${item.name}`,
                          name: `${item.name}`,
                        }))
                      : []
                }
                onChange={(event: any, newValue: any) => {
                  fetchOrganizationData(newValue?._id);
                  setValue('organization', newValue, { shouldValidate: true });
                }}
                isOptionEqualToValue={(option: any, value: any) =>
                  value ? option._id === value._id : false
                }
              />
            </Box>
          </Card>
        </ShowIf>

        <OrganizationInfoCard
          organizationData={organizationData}
          contacts={[]}
          currentOrder={{ ...currentOrder, orderType: OrderTypes.BILLING_ORDER }}
        />

        <BillingMilestonesSelector
          organizationId={organization?._id}
          value={values.milestones}
          currentOrderId={currentOrder?._id}
          onMilestonesChange={handleMilestonesChange}
          inUseMilestones={inUseMilestones}
        />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid xs={12} md={3}>
            <ShowIf condition={!!OVNumber}>
              <Card sx={{ px: 2, pb: 3, mb: 3 }}>
                <CardHeader sx={{ px: 0, pb: 1 }} title={t('Reference OV Number')} />
                <Box
                  color="text.secondary"
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    border: `1px solid ${
                      settings.themeMode === 'dark'
                        ? theme.palette.background.neutral
                        : theme.palette.background.neutral
                    }`,
                  }}
                >
                  <Box>{OVNumber}</Box>
                </Box>
              </Card>
            </ShowIf>
          </Grid>
        </Grid>

        <ShowIf condition={!!Object.keys(errors).length}>
          <Stack alignItems="flex-end" sx={{ mb: 1, mt: 5 }}>
            <List>
              {Object.values(errors).map((e: any, index: number) => (
                <ListItem key={index} sx={{ py: 0.5, color: 'error.main' }}>
                  <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                    <div
                      style={{
                        width: '5px',
                        height: '5px',
                        borderRadius: '50%',
                        backgroundColor: 'red',
                      }}
                    />
                  </ListItemIcon>
                  {JSON.stringify(e.message).replace(/"/g, '')}
                </ListItem>
              ))}
            </List>
          </Stack>
        </ShowIf>

        <Grid container>
          <Grid xs={4} sx={{ textAlign: 'left' }}>
            <Button
              variant="outlined"
              sx={{ mr: 1 }}
              onClick={() => {
                onCancel();
              }}
            >
              {t('Cancel')}
            </Button>
          </Grid>

          <Grid xs={8} sx={{ textAlign: 'right' }}>
            <ShowIf condition={!OVNumber}>
              <LoadingButton
                color="secondary"
                type="button"
                onClick={() => {
                  setValue('onlySave', true);
                  onSubmit();
                }}
                sx={{ mr: 1 }}
                variant="contained"
                loading={isSubmitting || isLoading}
              >
                {t('Save Draft')}
              </LoadingButton>
            </ShowIf>
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              onClick={() => {
                setValue('onlySave', false);
              }}
              loading={isSubmitting || isLoading}
            >
              {t('Next')}
            </LoadingButton>
          </Grid>
        </Grid>
      </FormProvider>
      <DialogError
        title={t('Error Creating Order')}
        error={error}
        open={dialogError.value}
        onClose={dialogError.onFalse}
      />
    </>
  );
};
